/**
 * @deprecated This component is deprecated and will be removed in a future release.
 * Please use the new Quimby UI component instead:
 *
 * Import: `import { Table } from '@ui';`
 *
 * Or specific path: `import { Table } from '@ui/molecules/Table';`
 *
 * See the new component for documentation and migration notes.
 */
import React, { createElement } from 'react';
import PropTypes from 'prop-types';
import clsx from 'classnames';
import { Button } from '@ui';
import Checkbox from '@components/v2/Checkbox';
import './Table.scss';

export const TableSize = {
  Auto: 'auto',
  Full: 'full'
};

const getCellTemplate = ({ column, row, rowIndex }) => {
  const key = `table-row-${column.dataKey}-${rowIndex}`;
  if (column.renderer) {
    return createElement(column.renderer, { item: row, dataKey: column.dataKey, index: rowIndex, key }, null);
  }
  return row[column.dataKey];
};

export default function Table({
  data,
  isGrouped,
  columns,
  variant = 'detail',
  size = TableSize.Auto,
  expandableRowRenderer,
  onRowClick,
  expandedRows,
  hover,
  selectable,
  selectedItems,
  onSelectionChange,
  onSelectAll,
  getItemId
}) {
  const dataGroups = !isGrouped ? [{ data }] : data;
  const allData = dataGroups.flatMap((group) => group.data);
  const allSelected =
    selectable && allData.length > 0 && allData.every((item) => selectedItems?.includes(getItemId(item)));
  const someSelected = selectable && selectedItems?.length > 0 && !allSelected;

  return (
    <div
      className={clsx('', {
        'qmb-table--01': variant === 'listing',
        'qmb-table--02': variant === 'detail'
      })}
      style={size === TableSize.Full ? { width: '100%' } : {}}>
      <table className="main-table" style={size === TableSize.Full ? { width: '100%' } : {}}>
        <thead>
          <tr>
            {selectable && (
              <th width="50px">
                <Checkbox
                  id="select-all"
                  checked={allSelected}
                  onChange={() => {
                    if (allSelected || someSelected) {
                      onSelectAll(false);
                    } else {
                      onSelectAll(true);
                    }
                  }}
                  label=""
                />
              </th>
            )}
            {columns.map((column) => (
              <th key={column.dataKey} width={column.width}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        {dataGroups.map((dataGroup, groupIdx) => (
          <tbody key={`table-group-${groupIdx}`}>
            {dataGroup.title && (
              <tr>
                <td className="groupTitle" colSpan={columns.length - (dataGroup.action ? 1 : 0)}>
                  {dataGroup.title}
                </td>
                {dataGroup.action && (
                  <td aria-label="add">
                    <Button onClick={dataGroup.action.onClick}>
                      <i className={dataGroup.action.icon} />
                      <span>{dataGroup.action.label}</span>
                    </Button>
                  </td>
                )}
              </tr>
            )}
            {dataGroup.data.map((row, rowIdx) => {
              const itemId = getItemId(row);
              const isSelected = selectedItems?.includes(itemId) || false;

              return (
                <React.Fragment key={`table-row-${groupIdx}:${rowIdx}`}>
                  <tr
                    key={`table-row-${groupIdx}:${rowIdx}`}
                    onClick={(event) => onRowClick && onRowClick({ item: row }, event)}
                    className={clsx({
                      'expandable-row': expandableRowRenderer,
                      hoverable: hover
                    })}>
                    {selectable && (
                      <td width="50px">
                        <Checkbox
                          id={`select-${itemId}`}
                          checked={isSelected}
                          onChange={(checked) => onSelectionChange(itemId, checked)}
                          label=""
                        />
                      </td>
                    )}
                    {columns.map((column) => (
                      <td
                        key={`table-row-${column.dataKey}-${rowIdx}`}
                        className={clsx({
                          cellAlignRight: column.align === 'right'
                        })}>
                        {getCellTemplate({ column, row, rowIndex: rowIdx })}
                      </td>
                    ))}
                  </tr>
                  {expandedRows && expandedRows[row.id] && expandableRowRenderer && (
                    <>{expandableRowRenderer({ item: row })}</>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  isGrouped: PropTypes.bool,
  variant: PropTypes.oneOf(['listing', 'detail']),
  size: PropTypes.oneOf([TableSize.Auto, TableSize.Full]),
  expandableRowRenderer: PropTypes.func,
  onRowClick: PropTypes.func,
  expandedRows: PropTypes.object,
  hover: PropTypes.bool,
  selectable: PropTypes.bool,
  selectedItems: PropTypes.array,
  onSelectionChange: PropTypes.func,
  onSelectAll: PropTypes.func,
  getItemId: PropTypes.func
};
Table.defaultProps = {
  variant: 'detail',
  isGrouped: false,
  size: TableSize.Auto,
  expandableRowRenderer: null,
  onRowClick: null,
  expandedRows: {},
  hover: false,
  selectable: false,
  selectedItems: [],
  onSelectionChange: null,
  onSelectAll: null,
  getItemId: (item) => item.id
};
